import { useEffect, useMemo, useState } from 'react'
import pick from 'lodash/pick'

import useDispatch from 'hooks/useDispatch'

import FiltersIcon from '../../../components/icons/Filters'
import { SearchQueryResult } from '../../../hooks/useQueries'
import { updateFilters } from '../../../actions/searchActions'

import CommonMoreFilters from '../common/MoreFilters'
import FilterButton from './FilterButton'

const clearedFilters: GQL_SearchFilters = {
  regions: null,
  driveTime: null,
  priceStart: null,
  priceEnd: null,
  subcategories: null,
  amenities: null,
  petFriendly: null,
  bedrooms: null,
  beds: null,
  instantBook: null,
  fullRefund: null,
  oneNightStayWeekday: null,
  oneNightStayWeekend: null,
}

const selectFilters = (filters: GQL_SearchFilters) => {
  return pick(filters, [
    'petFriendly',
    'regions',
    'driveTime',
    'priceStart',
    'priceEnd',
    'subcategories',
    'amenities',
    'bedrooms',
    'beds',
    'instantBook',
    'fullRefund',
    'oneNightStayWeekday',
    'oneNightStayWeekend',
  ])
}

type MoreFiltersPanelProps = {
  defaults: SearchQueryResult['data']['search']['defaults']
  minPrice: number
  maxPrice: number
  filters: GQL_SearchFilters
}

const MoreFilters = ({
  filters,
  defaults,
  minPrice,
  maxPrice,
}: MoreFiltersPanelProps) => {
  const dispatch = useDispatch()
  const initialState = useMemo(() => selectFilters(filters), [filters])
  const [selectedFilters, setSelectedFilters] = useState(initialState)

  useEffect(() => {
    setSelectedFilters(initialState)
  }, [initialState])

  const priceBounds = [minPrice, maxPrice] as [number, number]

  const handleFiltersChange = (nextFilters: Partial<GQL_SearchFilters>) => {
    const nextState = {
      ...selectedFilters,
      ...nextFilters,
    }

    setSelectedFilters(nextState)
  }

  const handlePriceRangeChange = (nextRange: [number, number]) => {
    setSelectedFilters({
      ...selectedFilters,
      priceStart: nextRange[0],
      priceEnd: nextRange[1],
    })
  }

  const handleApply = () => {
    dispatch(updateFilters(selectedFilters))
  }

  const handleClear = () => {
    setSelectedFilters(clearedFilters)
    dispatch(updateFilters(clearedFilters))
  }

  const handleReset = () => {
    setSelectedFilters(initialState)
  }

  const includeRegionsFilter =
    filters?.country != null && filters?.state != null

  const isActive = Object.values(selectedFilters).filter(Boolean).length > 0

  return (
    <FilterButton
      isActive={isActive}
      onApply={handleApply}
      onClear={handleClear}
      onReset={handleReset}
      label={
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              height: '20px',
              width: '20px',
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: '8px',
            }}
          >
            <FiltersIcon />
          </div>
          Filters
        </span>
      }
      panelActionsBorder
    >
      <CommonMoreFilters
        appliedFilters={filters}
        defaults={defaults}
        filterValuesState={{
          ...selectedFilters,
          priceBounds,
        }}
        includeRegionsFilter={includeRegionsFilter}
        handleFiltersChange={handleFiltersChange}
        handlePriceRangeChange={handlePriceRangeChange}
      />
    </FilterButton>
  )
}

export default MoreFilters
